import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { getRuntimeConfig, resolveSystemUserId } from '../config/runtimeConfig';

export default function AppStartupGuard({ children }) {
  const [status, setStatus] = useState('checking'); // checking | ok | setup_required
  const [details, setDetails] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await getRuntimeConfig();
        const resolved = await resolveSystemUserId(supabase);
        if (!mounted) return;

        // Only hard-stop on explicit missing system user, not transient network errors.
        if (!resolved?.id && String(resolved?.error || '').startsWith('system_user_not_found:')) {
          setDetails({
            systemUsername: cfg?.systemUsername || 'system',
            error: resolved.error,
          });
          setStatus('setup_required');
          return;
        }
        setStatus('ok');
      } catch {
        if (mounted) setStatus('ok');
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-6">
        <div className="text-sm text-slate-300">Starting GweffIT…</div>
      </div>
    );
  }

  if (status === 'setup_required') {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl border border-amber-500/30 bg-slate-800/80 p-5">
          <div className="text-xl font-black text-amber-200">Setup required</div>
          <div className="mt-2 text-sm text-slate-300">
            GweffIT couldn&apos;t resolve the system user used for featured/seeded trials.
          </div>
          <div className="mt-3 text-xs text-slate-400 space-y-1">
            <div>Fix one of these:</div>
            <div>1. Set `REACT_APP_SYSTEM_USER_ID` in runtime config/env</div>
            <div>2. Ensure `public.users.username = '{details?.systemUsername || 'system'}'` exists</div>
          </div>
          {details?.error && <div className="mt-3 text-xs text-amber-300">{details.error}</div>}
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-amber-600 hover:bg-amber-500 px-3 py-2 text-sm font-bold text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return children;
}
